<template>
  <v-dialog
    v-model="dialogModel"
    :retain-focus="false"
    content-class="mv-0 mh-0 pv-0 ph-0 d-flex flex-column justify-center align-center fill-height"
  >
    <v-card :width="smAndUp ? '600px' : undefined">
      <v-card-title>
        <template v-if="editOrg">
          <span class="text-h5">{{ 'Edit Organization' }}</span>
        </template>
        <template v-else>
          <span class="text-h5">{{ 'New Organization' }}</span>
        </template>
      </v-card-title>
      <v-tabs v-if="editOrg" v-model="activeTab" density="compact">
        <v-tab value="general">General</v-tab>
        <v-tab value="auth">Auth Settings</v-tab>
        <v-tab value="password-policy">Password Policy</v-tab>
      </v-tabs>
      <v-card-text>
        <v-window v-if="editOrg" v-model="activeTab">
          <v-window-item value="general">
            <v-form v-model="orgValid" ref="orgFormRef">
              <v-container>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-text-field
                      ref="idFieldRef"
                      :class="isDarkTheme ? 'text-input-white' : ''"
                      variant="outlined"
                      label="Identifier"
                      v-model="orgId"
                      :rules="editOrg ? [] : orgIdRules"
                      :disabled="!!editOrg"
                      :required="!editOrg"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-text-field
                      ref="nameFieldRef"
                      :class="isDarkTheme ? 'text-input-white' : ''"
                      variant="outlined"
                      label="Display Name"
                      v-model="orgName"
                      :rules="orgNameRules"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-text-field
                      :class="isDarkTheme ? 'text-input-white' : ''"
                      variant="outlined"
                      label="Sponsor Name"
                      v-model="sponsorName"
                      :rules="nameRules"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-text-field
                      :class="isDarkTheme ? 'text-input-white' : ''"
                      variant="outlined"
                      label="Sponsor Email"
                      v-model="sponsorEmail"
                      :rules="emailRules"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-text-field
                      :class="isDarkTheme ? 'text-input-white' : ''"
                      variant="outlined"
                      label="Company URL"
                      v-model="companyURL"
                      :rules="urlRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-checkbox
                      density="compact"
                      v-model="isArchived"
                      label="Archived"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-divider></v-divider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0"> Entitlements </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-checkbox
                      density="compact"
                      v-model="entitlementScenarioBuilder"
                      label="Scenario Builder Access"
                      class="pa-0"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pa-0">
                    <v-checkbox
                      density="compact"
                      v-model="entitlementVoiceCopilot"
                      label="Voice Co-pilot"
                      class="pa-0"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-window-item>
          <v-window-item value="auth">
            <v-container>
              <!-- SSO Status -->
              <v-row>
                <v-col cols="12" class="pa-0">
                  <div class="text-subtitle-1 font-weight-bold mb-2">
                    Single Sign-On (SSO)
                  </div>
                  <v-chip
                    v-if="ssoConfig?.sso_enabled"
                    color="success"
                    size="small"
                    class="mb-2"
                    >Configured</v-chip
                  >
                  <v-chip v-else size="small" class="mb-2"
                    >Not Configured</v-chip
                  >
                  <div v-if="ssoConfig?.sso_enabled" class="text-body-2 mb-2">
                    <div v-if="ssoConfig?.sso_provider_type">
                      <strong>Provider:</strong>
                      {{ ssoConfig.sso_provider_type }}
                    </div>
                    <div v-if="ssoConfig?.sso_domain">
                      <strong>Domain:</strong> {{ ssoConfig.sso_domain }}
                    </div>
                  </div>
                  <div class="d-flex ga-2">
                    <v-btn
                      size="small"
                      variant="outlined"
                      :loading="authActionLoading"
                      @click="handleConfigureSSO"
                    >
                      {{
                        ssoConfig?.sso_enabled
                          ? 'Reconfigure SSO'
                          : 'Configure SSO'
                      }}
                    </v-btn>
                    <v-btn
                      v-if="ssoConfig?.sso_enabled"
                      size="small"
                      variant="outlined"
                      color="error"
                      :loading="authActionLoading"
                      @click="handleRemoveSSO"
                    >
                      Remove SSO
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
              <v-divider class="my-4"></v-divider>
              <!-- MFA Policy -->
              <v-row>
                <v-col cols="12" class="pa-0">
                  <div class="text-subtitle-1 font-weight-bold mb-2">
                    Multi-Factor Authentication
                  </div>
                  <v-switch
                    v-model="mfaPolicy.enabled"
                    label="Enable MFA"
                    density="compact"
                    hide-details
                    class="mb-2"
                  ></v-switch>
                  <template v-if="mfaPolicy.enabled">
                    <v-select
                      v-model="mfaPolicy.enforcement"
                      :items="[
                        { title: 'Never', value: 'never' },
                        { title: 'Always', value: 'always' },
                      ]"
                      label="Enforcement"
                      variant="outlined"
                      density="compact"
                      class="mb-2"
                    ></v-select>
                    <div class="text-body-2 mb-1">Allowed Factors</div>
                    <v-checkbox
                      v-model="mfaFactorOtp"
                      label="Authenticator App (TOTP)"
                      density="compact"
                      hide-details
                      class="mb-2"
                    ></v-checkbox>
                    <v-slider
                      v-model="mfaPolicy.remember_device_days"
                      :min="0"
                      :max="90"
                      :step="1"
                      :label="`Remember device: ${mfaPolicy.remember_device_days} days`"
                      thumb-label
                      class="mt-2"
                    ></v-slider>
                  </template>
                  <v-btn
                    size="small"
                    variant="outlined"
                    :loading="authActionLoading"
                    @click="handleSaveMFA"
                    class="mt-2"
                  >
                    Save MFA Policy
                  </v-btn>
                </v-col>
              </v-row>
              <v-alert
                type="info"
                density="compact"
                variant="tonal"
                class="mt-4"
              >
                MFA applies to sponsor accounts using password-based login.
                Players using magic-link and SSO users are not affected.
              </v-alert>
            </v-container>
          </v-window-item>
          <v-window-item value="password-policy">
            <v-container>
              <div class="text-subtitle-1 font-weight-bold mb-2">
                Global Password Policy
              </div>
              <v-alert
                type="info"
                density="compact"
                variant="tonal"
                class="mb-4"
              >
                This policy applies to all users across all organizations.
              </v-alert>
              <v-select
                v-model="passwordPolicy.strength"
                :items="[
                  { title: 'None', value: 'none' },
                  { title: 'Low', value: 'low' },
                  { title: 'Fair', value: 'fair' },
                  { title: 'Good', value: 'good' },
                  { title: 'Excellent', value: 'excellent' },
                ]"
                label="Strength Level"
                variant="outlined"
                density="compact"
                class="mb-2"
              ></v-select>
              <v-text-field
                v-model.number="passwordPolicy.min_length"
                label="Minimum Length"
                variant="outlined"
                density="compact"
                type="number"
                :min="1"
                :max="128"
                class="mb-2"
              ></v-text-field>
              <v-switch
                v-model="passwordPolicy.history_enabled"
                label="Password History"
                density="compact"
                hide-details
                class="mb-2"
              ></v-switch>
              <v-text-field
                v-if="passwordPolicy.history_enabled"
                v-model.number="passwordPolicy.history_count"
                label="History Count"
                variant="outlined"
                density="compact"
                type="number"
                :min="1"
                :max="24"
                class="mb-2"
              ></v-text-field>
              <v-switch
                v-model="passwordPolicy.dictionary_check"
                label="Dictionary Check"
                density="compact"
                hide-details
                class="mb-2"
              ></v-switch>
              <v-switch
                v-model="passwordPolicy.personal_data_check"
                label="Personal Data Check"
                density="compact"
                hide-details
                class="mb-2"
              ></v-switch>
              <v-btn
                size="small"
                variant="outlined"
                :loading="authActionLoading"
                @click="handleSavePasswordPolicy"
                class="mt-2"
              >
                Save Password Policy
              </v-btn>
            </v-container>
          </v-window-item>
        </v-window>
        <!-- New org form (no tabs) -->
        <v-form v-if="!editOrg" v-model="orgValid" ref="orgFormRef">
          <v-container>
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-text-field
                  ref="idFieldRef"
                  :class="isDarkTheme ? 'text-input-white' : ''"
                  variant="outlined"
                  label="Identifier"
                  v-model="orgId"
                  :rules="orgIdRules"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-text-field
                  ref="nameFieldRef"
                  :class="isDarkTheme ? 'text-input-white' : ''"
                  variant="outlined"
                  label="Display Name"
                  v-model="orgName"
                  :rules="orgNameRules"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-text-field
                  :class="isDarkTheme ? 'text-input-white' : ''"
                  variant="outlined"
                  label="Sponsor Name"
                  v-model="sponsorName"
                  :rules="nameRules"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-text-field
                  :class="isDarkTheme ? 'text-input-white' : ''"
                  variant="outlined"
                  label="Sponsor Email"
                  v-model="sponsorEmail"
                  :rules="emailRules"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-text-field
                  :class="isDarkTheme ? 'text-input-white' : ''"
                  variant="outlined"
                  label="Company URL"
                  v-model="companyURL"
                  :rules="urlRules"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-checkbox
                  density="compact"
                  v-model="entitlementScenarioBuilder"
                  label="Scenario Builder Access"
                  class="pa-0"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="pa-0">
                <v-checkbox
                  density="compact"
                  v-model="entitlementVoiceCopilot"
                  label="Voice Co-pilot"
                  class="pa-0"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancelOrgCommit"> Cancel </v-btn>
        <v-btn
          v-if="activeTab === 'general' || !editOrg"
          text
          @click="saveOrgCommit"
          :disabled="orgValid === false"
        >
          {{ editOrg ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { inject, ref, computed, watch } from 'vue'
import { useDisplay, useTheme } from 'vuetify'
import { useAuth0 } from '../utils/auth/index.js'
import {
  createOrg,
  updateOrg,
  getOrgSSOConfig,
  createSSOSetupTicket,
  removeOrgSSO,
  getOrgMFAPolicy,
  updateOrgMFAPolicy,
  getPasswordPolicy,
  updatePasswordPolicy,
} from '../utils/data.js'
import { entitlementTypes, orgHasEntitlement } from '../utils/authUtils.js'
export default {
  name: 'OrgEditDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    editOrg: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  emits: ['update:modelValue', 'org-saved'],
  setup(props, context) {
    // Logger
    const logger = inject('vuejs3-logger')

    const dialogModel = ref(false)

    const orgId = ref('')
    const orgName = ref('')
    const sponsorName = ref('')
    const sponsorEmail = ref('')
    const companyURL = ref('')
    const isArchived = ref(false)

    // Entitlements
    const entitlementScenarioBuilder = ref(false)
    const entitlementVoiceCopilot = ref(false)

    // Auth Settings (tabs + SSO/MFA/Password Policy)
    const activeTab = ref('general')
    const authActionLoading = ref(false)
    const ssoConfig = ref(null)
    const mfaPolicy = ref({
      enabled: false,
      enforcement: 'never',
      allowed_factors: ['otp'],
      remember_device_days: 30,
    })
    const passwordPolicy = ref({
      strength: 'good',
      min_length: 8,
      history_enabled: false,
      history_count: 5,
      dictionary_check: false,
      personal_data_check: false,
    })

    // Computed refs for individual MFA factor checkboxes
    const mfaFactorOtp = computed({
      get: () => mfaPolicy.value.allowed_factors.includes('otp'),
      set: (val) => {
        const factors = mfaPolicy.value.allowed_factors
        if (val && !factors.includes('otp')) {
          mfaPolicy.value.allowed_factors = [...factors, 'otp']
        } else if (!val) {
          mfaPolicy.value.allowed_factors = factors.filter((f) => f !== 'otp')
        }
      },
    })

    // Auto focus handling

    const idFieldRef = ref(null)
    const nameFieldRef = ref(null)

    //Theme Handling
    const theme = useTheme()
    const isDarkTheme = ref(theme.current.value === 'dark')
    watch(theme.current, () => {
      if (theme.current.value === 'dark') {
        isDarkTheme.value = true
      } else {
        isDarkTheme.value = false
      }
    })

    // Responsive Sizing Logic
    const { smAndUp } = useDisplay()

    // V-Model 2-Way Binding
    watch(
      () => props.modelValue,
      (val) => {
        dialogModel.value = val
      },
      { immediate: true }
    )

    watch(dialogModel, (val) => {
      context.emit('update:modelValue', val) // Pass change up to parent component

      if (val) {
        // Populate the fields with org to edit (if applicable)
        orgId.value = props.editOrg?.id ?? ''
        orgName.value = props.editOrg?.displayName ?? ''
        sponsorName.value = props.editOrg?.sponsorName ?? ''
        sponsorEmail.value = props.editOrg?.sponsorEmail ?? ''
        companyURL.value = props.editOrg?.companyURL ?? ''
        isArchived.value = props.editOrg?.archived ?? false

        // Entitlements
        entitlementScenarioBuilder.value = orgHasEntitlement({
          org: props.editOrg,
          entitlementType: entitlementTypes.SCENARIO_BUILDER,
        })
        entitlementVoiceCopilot.value = orgHasEntitlement({
          org: props.editOrg,
          entitlementType: entitlementTypes.VOICE_COPILOT,
        })
        // Reset tab to general
        activeTab.value = 'general'

        // Fetch auth config data when editing an org
        if (props.editOrg?.id) {
          fetchAuthConfigData(props.editOrg.id)
        }

        // Autofocus the proper UI element after dialog becomes visible
        setTimeout(() => {
          if (props.editOrg) {
            nameFieldRef.value?.focus()
          } else {
            idFieldRef.value?.focus()
          }
        }, 250)
      }
    })

    // Form Validation
    const orgFormRef = ref(null)
    var validIdRegex = /^[a-z0-9-]+$/
    const orgValid = ref(false)
    const orgIdRules = ref([
      (v) =>
        v.search(validIdRegex) !== -1 ||
        'Only lowercase letters, numbers and hyphens allowed.',
      (v) =>
        '0123456789-_'.includes(v.charAt(0)) === false ||
        'Must start with a lowercase letter.',
      (v) =>
        (v.length > 0 && v.length <= 64) ||
        'Must have a length from 1 to 64 characters.',
    ])
    const orgNameRules = ref([
      (v) => {
        if (v) {
          return (
            v.length <= 256 ||
            'Display name must be 256 characters long or less'
          )
        }
        return true
      },
    ])
    const emailRules = ref([
      (v) => {
        if (v) {
          if (/.+@.+/.test(v) === false) {
            return 'E-mail must be valid'
          } else if (v !== v.trim()) {
            return 'No leading or trailing spaces allowed'
          }
        }
        return true
      },
    ])
    const nameRules = ref([
      (v) => {
        if (v) {
          return (
            v.length <= 256 ||
            'Sponsor name must be 256 characters long or less'
          )
        }
        return true
      },
    ])
    const urlRules = ref([
      (v) => {
        if (v) {
          try {
            new URL(v)
            return true
          } catch {
            return 'Must be a valid URL'
          }
        }
        return true
      },
    ])

    // UI functions
    const resetFormFields = () => {
      // Reset the fields
      orgId.value = ''
      orgName.value = ''
      sponsorName.value = ''
      sponsorEmail.value = ''
      companyURL.value = ''
      isArchived.value = false
      entitlementScenarioBuilder.value = false
      entitlementVoiceCopilot.value = false

      // Reset auth config state
      activeTab.value = 'general'
      ssoConfig.value = null
      mfaPolicy.value = {
        enabled: false,
        enforcement: 'never',
        allowed_factors: ['otp'],
        remember_device_days: 30,
      }
      passwordPolicy.value = {
        strength: 'good',
        min_length: 8,
        history_enabled: false,
        history_count: 5,
        dictionary_check: false,
        personal_data_check: false,
      }
      authActionLoading.value = false
    }

    // Save Functions
    const { getAccessTokenSilently, user } = useAuth0()
    const saveOrgCommit = async () => {
      // Commit the save

      let saved
      // Update Org
      if (props.editOrg) {
        saved = await updateOrg(
          await getAccessTokenSilently(),
          user.value?.sub,
          {
            id: orgId.value,
            displayName: orgName.value,
            sponsorName: sponsorName.value,
            sponsorEmail: sponsorEmail.value,
            companyURL: companyURL.value || null,
            archived: isArchived.value,
            entitlements: {
              [entitlementTypes.SCENARIO_BUILDER]:
                entitlementScenarioBuilder.value,
              [entitlementTypes.VOICE_COPILOT]: entitlementVoiceCopilot.value,
            },
          }
        )
        logger.debug(`Updated org '${orgId.value}': ${JSON.stringify(saved)}`)
      } else {
        saved = await createOrg(
          await getAccessTokenSilently(),
          user.value?.sub,
          {
            id: orgId.value,
            displayName: orgName.value,
            sponsorName: sponsorName.value,
            sponsorEmail: sponsorEmail.value,
            companyURL: companyURL.value || null,
            archived: isArchived.value,
            entitlements: {
              [entitlementTypes.SCENARIO_BUILDER]:
                entitlementScenarioBuilder.value,
              [entitlementTypes.VOICE_COPILOT]: entitlementVoiceCopilot.value,
            },
          }
        )
        if (saved) {
          logger.debug(`Created org '${orgId.value}': ${JSON.stringify(saved)}`)
        } else {
          logger.debug(
            `Org '${orgId.value}' not created. Check that id is unique.`
          )
          // Add validation rule for this in-use id value
          const badOrgId = orgId.value
          orgIdRules.value?.push((v) => v !== badOrgId || 'ID already in use.')
          // Force re-validation
          orgFormRef.value?.validate()
        }
      }

      if (saved) {
        // Signal data updated
        context.emit('org-saved', saved)

        // Close the dialog
        dialogModel.value = false

        resetFormFields()
      }
    }

    const cancelOrgCommit = () => {
      // Close the dialog
      dialogModel.value = false

      resetFormFields()
    }

    // --- Auth Config: Fetch, SSO, MFA, Password Policy ---

    const fetchAuthConfigData = async (orgId) => {
      try {
        const token = await getAccessTokenSilently()
        const userId = user.value?.sub

        const [ssoResult, mfaResult, pwResult] = await Promise.all([
          getOrgSSOConfig(token, userId, { orgId }),
          getOrgMFAPolicy(token, userId, { orgId }),
          getPasswordPolicy(token, userId),
        ])

        if (ssoResult) {
          ssoConfig.value = ssoResult
        }
        if (mfaResult) {
          mfaPolicy.value = {
            enabled: mfaResult.enabled ?? false,
            enforcement: mfaResult.enforcement ?? 'never',
            allowed_factors: mfaResult.allowed_factors ?? ['otp'],
            remember_device_days: mfaResult.remember_device_days ?? 30,
          }
        }
        if (pwResult) {
          passwordPolicy.value = {
            strength: pwResult.strength ?? 'good',
            min_length: pwResult.min_length ?? 8,
            history_enabled: pwResult.history_enabled ?? false,
            history_count: pwResult.history_count ?? 5,
            dictionary_check: pwResult.dictionary_check ?? false,
            personal_data_check: pwResult.personal_data_check ?? false,
          }
        }
      } catch (err) {
        logger.error('Failed to fetch auth config data:', err)
      }
    }

    const handleConfigureSSO = async () => {
      const orgId = props.editOrg?.id
      if (!orgId) return

      authActionLoading.value = true
      try {
        const token = await getAccessTokenSilently()
        const userId = user.value?.sub
        const result = await createSSOSetupTicket(token, userId, {
          orgId,
        })
        if (result?.ticket) {
          window.open(result.ticket, '_blank')
        } else {
          logger.warn('No SSO setup ticket returned')
        }
      } catch (err) {
        logger.error('Failed to configure SSO:', err)
      } finally {
        authActionLoading.value = false
      }
    }

    const handleRemoveSSO = async () => {
      const orgId = props.editOrg?.id
      if (!orgId) return

      authActionLoading.value = true
      try {
        const token = await getAccessTokenSilently()
        const userId = user.value?.sub
        await removeOrgSSO(token, userId, { orgId })
        ssoConfig.value = null
      } catch (err) {
        logger.error('Failed to remove SSO:', err)
      } finally {
        authActionLoading.value = false
      }
    }

    const handleSaveMFA = async () => {
      const orgId = props.editOrg?.id
      if (!orgId) return

      authActionLoading.value = true
      try {
        const token = await getAccessTokenSilently()
        const userId = user.value?.sub
        await updateOrgMFAPolicy(token, userId, {
          orgId,
          policy: mfaPolicy.value,
        })
      } catch (err) {
        logger.error('Failed to save MFA policy:', err)
      } finally {
        authActionLoading.value = false
      }
    }

    const handleSavePasswordPolicy = async () => {
      authActionLoading.value = true
      try {
        const token = await getAccessTokenSilently()
        const userId = user.value?.sub
        await updatePasswordPolicy(token, userId, {
          policy: passwordPolicy.value,
        })
      } catch (err) {
        logger.error('Failed to save password policy:', err)
      } finally {
        authActionLoading.value = false
      }
    }

    return {
      dialogModel,
      isDarkTheme,
      // Autofocus handling
      idFieldRef,
      nameFieldRef,
      // Responsive Sizing
      smAndUp,
      // Form Field Values
      orgId,
      orgName,
      sponsorName,
      sponsorEmail,
      companyURL,
      isArchived,
      // Entitlements
      entitlementScenarioBuilder,
      entitlementVoiceCopilot,
      // Auth Settings
      activeTab,
      authActionLoading,
      ssoConfig,
      mfaPolicy,
      mfaFactorOtp,
      passwordPolicy,
      handleConfigureSSO,
      handleRemoveSSO,
      handleSaveMFA,
      handleSavePasswordPolicy,
      // Form Validation
      orgFormRef,
      orgValid,
      orgIdRules,
      orgNameRules,
      emailRules,
      nameRules,
      urlRules,
      saveOrgCommit,
      cancelOrgCommit,
    }
  },
}
</script>

<style lang="scss" scoped>
.text-input-white {
  :deep(.v-field__input) {
    color: rgba(255, 255, 255, 0.6) !important;
  }
}
</style>
